import React, { useEffect, createElement } from 'react';
// The App component's contents are currently a placeholder — please update this file first for a new design / component!
import { motion, useScroll, useTransform } from 'framer-motion';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { PromoStrip } from './components/PromoStrip';
import { Hero } from './components/Hero';
import { LogoSection } from './components/LogoSection';
import { SellingPoints } from './components/SellingPoints';
import { RequestManagement } from './components/RequestManagement';
import { ClientExperience } from './components/ClientExperience';
import { PigeonAPI } from './components/PigeonAPI';
import { GradientEffect } from './components/GradientEffect';
import { SecuritySection } from './components/SecuritySection';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
import { DataStreamVisualization } from './components/DataStreamVisualization';
import { DocumentOrbitAnimation } from './components/DocumentOrbitAnimation';
import { DocumentStreamVisualization } from './components/DocumentStreamVisualization';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { Support } from './pages/Support';
import { Success } from './pages/Success';
import { ImmigrationLawRedirect, SubjectivitiesRedirect } from './components/RedirectPages';
import { ImmigrationThankYou } from './pages/ImmigrationThankYou';
import { isMobile, mobileDebounce } from './utils/deviceDetection';
export function App() {
  // Add Inter font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  // Add tracking script
  useEffect(() => {
    // Check if script already exists
    if (window.reb2b) return;
    // Create script element
    const script = document.createElement('script');
    script.innerHTML = `!function(key) {if (window.reb2b) return;window.reb2b = {loaded: true};var s = document.createElement("script");s.async = true;s.src = "https://b2bjsstore.s3.us-west-2.amazonaws.com/b/" + key + "/" + key + ".js.gz";document.getElementsByTagName("script")[0].parentNode.insertBefore(s, document.getElementsByTagName("script")[0]);}("QO92DHKE84N7")`;
    // Add script to head
    document.head.appendChild(script);
    // Cleanup function
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  // Use debounced scroll for mobile
  const {
    scrollY
  } = useScroll({
    // Use a more efficient scroll handling on mobile
    container: undefined,
    offset: ['start', 'end'],
    layoutEffect: !isMobile()
  });
  const opacity = useTransform(scrollY, [0, 100], [1, 0]);
  return <BrowserRouter data-id="element-0">
      <Routes data-id="element-1">
        <Route path="/privacy-policy" element={<PrivacyPolicy data-id="element-3" />} data-id="element-2" />
        <Route path="/terms-of-service" element={<TermsOfService data-id="element-5" />} data-id="element-4" />
        <Route path="/support" element={<Support data-id="element-7" />} data-id="element-6" />
        <Route path="/success" element={<Success data-id="element-9" />} data-id="element-8" />
        <Route path="/immigration-law" element={<ImmigrationLawRedirect data-id="element-11" />} data-id="element-10" />
        <Route path="/subjectivities" element={<SubjectivitiesRedirect data-id="element-13" />} data-id="element-12" />
        <Route path="/immigration-thank-you" element={<ImmigrationThankYou data-id="element-15" />} data-id="element-14" />
        <Route path="/" element={<div className="font-[Inter,ui-sans-serif,system-ui] text-[#0B1220] overflow-x-hidden relative bg-gradient-to-b from-[#f8faff] to-[#f0f5ff]" data-id="element-17">
              {/* Cinematic frame markers - hidden on mobile for performance */}
              {!isMobile() && <>
                  <div className="fixed inset-x-0 top-0 h-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-18"></div>
                  <div className="fixed inset-x-0 bottom-0 h-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-19"></div>
                  <div className="fixed inset-y-0 left-0 w-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-20"></div>
                  <div className="fixed inset-y-0 right-0 w-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-21"></div>
                </>}
              {/* Dynamic background elements - simplified for mobile */}
              <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-22">
                <div className="absolute top-0 right-0 w-[80vw] h-[80vh] rounded-full bg-gradient-to-b from-[#2E6BFF]/5 to-transparent opacity-40 blur-[120px]" data-id="element-23"></div>
                <div className="absolute bottom-0 left-0 w-[70vw] h-[70vh] rounded-full bg-gradient-to-t from-[#36D1DC]/5 to-transparent opacity-40 blur-[100px]" data-id="element-24"></div>
                {/* Simplified animation for mobile */}
                <motion.div className="absolute top-1/4 left-1/4 w-[50vw] h-[50vh] rounded-full bg-gradient-to-r from-[#0BB489]/5 to-transparent opacity-30 blur-[80px]" animate={isMobile() ? {
            opacity: [0.2, 0.3, 0.2]
          } // Simpler animation for mobile
          : {
            x: [0, 20, 0],
            y: [0, -20, 0]
          } // Full animation for desktop
          } transition={{
            duration: isMobile() ? 10 : 20,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut'
          }} style={{
            willChange: isMobile() ? 'opacity' : 'transform' // Optimize what changes
          }} data-id="element-25"></motion.div>
              </div>
              <Navbar data-id="element-26" />
              <PromoStrip data-id="element-27" />
              <main className="relative z-10" data-id="element-28">
                <Hero data-id="element-29" />
                <section id="how-it-works" data-id="element-30">
                  <SellingPoints data-id="element-31" />
                </section>
                <RequestManagement data-id="element-32" />
                <ClientExperience data-id="element-33" />
                <section id="integrations" data-id="element-34">
                  <PigeonAPI key="pigeon-api-component" data-id="element-35" />
                </section>
                <GradientEffect data-id="element-36" />
                <section id="security" data-id="element-37">
                  <SecuritySection data-id="element-38" />
                </section>
                <FinalCTA data-id="element-39" />
              </main>
              <Footer data-id="element-40" />
            </div>} data-id="element-16" />
      </Routes>
    </BrowserRouter>;
}